import _ from "lodash";
import React, { useRef, useState, useMemo } from "react";
import { connect } from "react-redux";

import {
  Modal,
  SearchInput,
  ChecklistDropdown,
  FilterLabelButton,
} from "@toddle-edu/ds-web";

import { useI18n } from "Hooks";
import { getLabelValueForMultiFilterLabelButton } from "Utils/getLabelValue";
import CurriculumProgramLogo from "AppComponents/CurriculumProgramLogo";
import { PARENT } from "Constants/stringConstants";

import {
  createPersonalChat,
  goToChatIdRoute,
  handleCreateNewChat,
} from "MessagingV2/modules/MessagingModule";
import {
  MESSAGING_MODULE_OPENED_FROM,
  getIsMessagingWidget,
  recordMessagingChatAccessed,
  isMessagingCurriculumWiseSplitEnabled,
  isMessagingRevempEnabled,
  getChatsAndChannelEnabledCurriculumPrograms,
} from "MessagingV2/modules/Utils";
import { goToRelativeRoute } from "modules/Services";
import { getUserType } from "modules/CommonSelectors";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import { setMessagingChatId } from "Login/modules/LoginModule";

import UsersPopover from "./components/UsersPopover";
import classes from "./CreateNewChatModal.scss";

const styles = {
  modalBodyStyle: {
    padding: 0,
  },
};

const getModalStyle = ({ isUIRevempEnabled }) => {
  if (isUIRevempEnabled) {
    return {
      width: "528px",
      height: "600px",
    };
  }

  return {
    width: "600px",
    height: "480px",
  };
};

const CreateNewChatModal = props => {
  const { onCloseModal, handleCreateNewChat } = props;
  const isUIRevempEnabled = isMessagingRevempEnabled();

  const { t } = useI18n(["common", "classRoom", "project"]);
  const containerRef = useRef(null);

  const { screenMode } = useChatOptions();

  const isWidget = getIsMessagingWidget({ screenMode });

  const onUserClick = async user => {
    const userId = _.get(user, "id");
    const userType = _.get(user, "type");

    recordMessagingChatAccessed({
      from: isWidget
        ? MESSAGING_MODULE_OPENED_FROM.MAGIC_TRAY
        : MESSAGING_MODULE_OPENED_FROM.MESSAGING_MODULE,
      currentUserType: getUserType(),
      recipientUserType: userType,
    });
    handleCreateNewChat({ id: userId, isWidget });
    onCloseModal();
  };

  const [searchText, setSearchText] = useState("");

  const curriculumOptions = useMemo(
    () =>
      _.map(getChatsAndChannelEnabledCurriculumPrograms(), program => ({
        label: program.label,
        value: program.id,
        prefixElement: (
          <CurriculumProgramLogo
            curriculumProgramId={program.id}
            size="small"
          />
        ),
      })),
    []
  );

  const showCurriculumFilter = _.size(curriculumOptions) > 1;

  const [selectedCurriculumIds, setSelectedCurriculumIds] = useState(() =>
    _.map(curriculumOptions, "value")
  );

  const placeholder = `${t("project:search_for_a_label", {
    label: t("common:user"),
  })} ...`;

  const modalStyle = getModalStyle({ isUIRevempEnabled });

  return (
    <Modal
      height={modalStyle.height}
      width={modalStyle.width}
      onClose={onCloseModal}
      dsVersion="2.0"
    >
      <Modal.Header
        title={t("classRoom:start_new_conversation")}
        hasSeparator={false}
      />
      <Modal.Body style={styles.modalBodyStyle} isScrollable>
        <div className={classes.searchInputContainerV2}>
          <SearchInput
            onChange={setSearchText}
            placeholder={placeholder}
            value={searchText}
            showLeadingIcon={true}
            size="medium"
            dsVersion="2.0"
          />
          {showCurriculumFilter ? (
            <div className={classes.curriculumFilterContainer}>
              <ChecklistDropdown
                options={curriculumOptions}
                value={selectedCurriculumIds}
                onChange={setSelectedCurriculumIds}
                isSearchEnabled
                hasAvatar
                showSelectedOptionsOnTop={false}
                dsVersion="2.0"
              >
                <FilterLabelButton
                  size="medium"
                  variant="neutral"
                  type="outlined"
                  label={t("common:school")}
                  labelValue={getLabelValueForMultiFilterLabelButton({
                    selectedOptions: selectedCurriculumIds,
                    options: curriculumOptions,
                    t,
                    noneText: t("common:none"),
                  })}
                  dsVersion="2.0"
                />
              </ChecklistDropdown>
            </div>
          ) : null}
        </div>
        <div className={classes.usersPopoverContainer} ref={containerRef}>
          <UsersPopover
            containerRef={containerRef}
            searchText={searchText}
            onUserClick={onUserClick}
            includePaidUsersOnly={
              _.isEqual(getUserType(), PARENT)
                ? false
                : isMessagingCurriculumWiseSplitEnabled()
            }
            includePaidFamily={false}
            curriculumProgramIds={
              showCurriculumFilter ? selectedCurriculumIds : undefined
            }
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = state => {
  return {
    currentUserRole: getUserType(state),
  };
};

const mapActionCreators = {
  createPersonalChat,
  goToRelativeRoute,
  setMessagingChatId,
  goToChatIdRoute,
  handleCreateNewChat,
};

export default connect(mapStateToProps, mapActionCreators)(CreateNewChatModal);
